import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Mail, CheckCircle2, Save, Unlink2, Send, Eye, EyeOff, Users } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/intl";

export default function BrevoSettings() {
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [listId, setListId] = useState("");
  const [fieldId, setFieldId] = useState("newsletter_optin");
  const [enabled, setEnabled] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/brevo/status");
      setStatus(r.data);
      if (r.data?.configured) {
        setListId(r.data.list_id ? String(r.data.list_id) : "");
        setFieldId(r.data.newsletter_field_id || "newsletter_optin");
        setEnabled(r.data.enabled !== false);
      }
    } catch (e) {
      toast.error("Could not load Brevo status", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function save() {
    if (!apiKey.trim() && !status?.configured) {
      toast.error("Paste your Brevo API key first");
      return;
    }
    if (!listId.trim()) {
      toast.error("Enter the Brevo list ID");
      return;
    }
    setSaving(true);
    try {
      const body = {
        list_id: Number(listId),
        newsletter_field_id: fieldId.trim() || "newsletter_optin",
        enabled,
      };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const r = await api.post("/brevo/credentials", body);
      setStatus(r.data);
      setApiKey("");
      toast.success("Brevo connected", {
        description: `List "${r.data.list_name}" — newsletter sync ${enabled ? "ON" : "PAUSED"}`,
      });
    } catch (e) {
      toast.error("Brevo verification failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  }

  async function disconnect() {
    if (!confirm("Disconnect Brevo? New intake submissions will no longer be synced.")) return;
    setDisconnecting(true);
    try {
      await api.delete("/brevo/credentials");
      await refresh();
      toast.success("Brevo disconnected");
    } catch (e) {
      toast.error("Disconnect failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setDisconnecting(false);
    }
  }

  async function runTest() {
    if (!testEmail.trim()) {
      toast.error("Enter a test email");
      return;
    }
    setTestBusy(true);
    try {
      const r = await api.post("/brevo/test-sync", {
        email: testEmail.trim(),
        first_name: "Test",
        last_name: "Subscriber",
      });
      if (r.data?.ok) {
        toast.success("Brevo accepted the contact", {
          description: `brevo_id=${r.data.brevo_id ?? "—"}; check list in Brevo dashboard`,
        });
      } else {
        toast.error("Brevo refused the contact", {
          description: r.data?.error || `HTTP ${r.data?.status_code}`,
        });
      }
    } catch (e) {
      toast.error("Test sync failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setTestBusy(false);
    }
  }

  async function runBackfill() {
    if (!confirm(
      "Scan all past intake submissions and push every newsletter-opted-in contact to your Brevo list?\n\n"
      + "Safe to run repeatedly — already-synced contacts are skipped, and Brevo merges duplicates by email.",
    )) return;
    setBackfillBusy(true);
    setBackfillResult(null);
    try {
      const r = await api.post("/brevo/backfill", null, { timeout: 120000 });
      setBackfillResult(r.data);
      const { synced, eligible, already_synced, failure_count } = r.data || {};
      if ((synced || 0) > 0) {
        toast.success(`Backfilled ${synced} contact${synced === 1 ? "" : "s"} to Brevo`, {
          description: `${eligible} eligible · ${already_synced} already-synced · ${failure_count || 0} failed`,
        });
      } else if ((eligible || 0) === 0) {
        toast.info("No newsletter opt-ins found in past submissions");
      } else if ((already_synced || 0) === eligible) {
        toast.info("All eligible contacts were already synced", {
          description: "Nothing new to push.",
        });
      } else {
        toast.warning("Backfill ran but synced zero new contacts", {
          description: `${failure_count || 0} failures — check the failures list below.`,
        });
      }
    } catch (e) {
      toast.error("Backfill failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBackfillBusy(false);
    }
  }

  const configured = !!status?.configured;

  return (
    <Card className="p-6 sm:p-8 rounded-lg border border-zinc-200 shadow-none" data-testid="brevo-settings-card">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
        <Mail className="h-3.5 w-3.5" /> Brevo — Newsletter sync
      </div>
      <h2 className="mt-2 font-display text-xl font-bold tracking-tight text-zinc-950">
        Brevo newsletter list
      </h2>
      <p className="mt-1 text-sm text-zinc-600">
        When a client ticks the "Subscribe to quarterly newsletter" checkbox on their intake form, we'll
        add them to the configured Brevo list automatically. Duplicate-safe (Brevo merges by email).
      </p>

      {configured ? (
        <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
          <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50">
            <div className="text-2xs uppercase tracking-wider text-zinc-500">Status</div>
            <div className="mt-1 inline-flex items-center gap-1.5 text-emerald-700 font-medium">
              <CheckCircle2 className="h-4 w-4" />
              {status.enabled !== false ? "Active" : "Paused"}
            </div>
          </div>
          <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50">
            <div className="text-2xs uppercase tracking-wider text-zinc-500">List</div>
            <div className="mt-1 font-mono-num text-zinc-900">
              {status.list_name || "—"} (#{status.list_id})
            </div>
          </div>
          <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50">
            <div className="text-2xs uppercase tracking-wider text-zinc-500">Last verified</div>
            <div className="mt-1 font-mono-num text-zinc-900 text-xs">
              {status.last_verified_iso ? fmtDateTime(status.last_verified_iso) : "—"}
            </div>
            {status.last_account_email && (
              <div className="mt-0.5 text-2xs text-zinc-500 truncate">{status.last_account_email}</div>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-4 border border-amber-200 bg-amber-50 rounded p-3 text-sm text-amber-900">
          Not connected. Paste your API key + list ID below.
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">
            {configured ? "Replace API key (leave blank to keep)" : "API key"}
          </Label>
          <div className="mt-1.5 relative">
            <Input
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              type={showKey ? "text" : "password"}
              placeholder={configured ? "•••••••••••" : "xkeysib-…"}
              className="border-zinc-300 font-mono-num pr-9"
              data-testid="brevo-key-input"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              onClick={() => setShowKey((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-900"
              tabIndex={-1}
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">List ID</Label>
          <Input
            value={listId}
            onChange={(e) => setListId(e.target.value.replace(/\D/g, ""))}
            placeholder="4"
            className="border-zinc-300 mt-1.5 font-mono-num"
            data-testid="brevo-list-id"
          />
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div className="sm:col-span-2">
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">
            Intake-form field ID to trigger sync
          </Label>
          <Input
            value={fieldId}
            onChange={(e) => setFieldId(e.target.value)}
            placeholder="newsletter_optin"
            className="border-zinc-300 mt-1.5 font-mono-num"
            data-testid="brevo-field-id"
          />
          <p className="mt-1 text-2xs text-zinc-500">
            Field ID on your intake template (default <span className="font-mono-num">newsletter_optin</span>).
            Only checkbox-ticked submissions sync.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="brevo-enabled-toggle"
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="brevo-enabled-toggle"
          />
          <Label htmlFor="brevo-enabled-toggle" className="cursor-pointer">
            {enabled ? "Sync active" : "Paused"}
          </Label>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-zinc-950 hover:bg-zinc-800 text-white"
          data-testid="brevo-save-btn"
        >
          <Save className="h-4 w-4 mr-1.5" />
          {saving ? "Verifying…" : configured ? "Update & re-verify" : "Save & verify"}
        </Button>
        {configured && (
          <Button
            variant="outline"
            onClick={disconnect}
            disabled={disconnecting}
            className="border-red-300 text-red-700 hover:bg-red-50"
            data-testid="brevo-disconnect-btn"
          >
            <Unlink2 className="h-4 w-4 mr-1.5" /> Disconnect
          </Button>
        )}
      </div>

      {configured && (
        <div className="mt-5 border-t border-zinc-200 pt-4">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-2">
            Test sync
          </div>
          <div className="flex gap-2">
            <Input
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              type="email"
              placeholder="test@yourdomain.com"
              className="border-zinc-300"
              data-testid="brevo-test-email"
            />
            <Button onClick={runTest} disabled={testBusy} variant="outline" data-testid="brevo-test-btn">
              <Send className="h-4 w-4 mr-1.5" /> {testBusy ? "Syncing…" : "Add to list"}
            </Button>
          </div>
          <p className="mt-2 text-2xs text-zinc-500">
            Adds a fake "Test Subscriber" with the entered email to your Brevo list (idempotent — running
            twice with the same email is safe).
          </p>
        </div>
      )}

      {configured && (
        <div className="mt-5 border-t border-zinc-200 pt-4" data-testid="brevo-backfill-section">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-2">
            Backfill from past intake submissions
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={runBackfill}
              disabled={backfillBusy}
              variant="outline"
              className="border-zinc-300"
              data-testid="brevo-backfill-btn"
            >
              <Users className="h-4 w-4 mr-1.5" />
              {backfillBusy ? "Scanning submissions…" : "Run backfill"}
            </Button>
            <p className="text-2xs text-zinc-500 max-w-md leading-relaxed">
              Finds every submitted intake form where{" "}
              <span className="font-mono-num text-zinc-700">{fieldId || "newsletter_optin"}</span>{" "}
              was ticked but Brevo wasn't yet configured (or sync was skipped) and pushes them all to your
              list in one go. Already-synced contacts are skipped.
            </p>
          </div>

          {backfillResult && (
            <div
              className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm"
              data-testid="brevo-backfill-result"
            >
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <div className="text-2xs uppercase tracking-wider text-zinc-500">Scanned</div>
                  <div className="font-mono-num text-zinc-900">{backfillResult.scanned ?? 0}</div>
                </div>
                <div>
                  <div className="text-2xs uppercase tracking-wider text-zinc-500">Opted-in</div>
                  <div className="font-mono-num text-zinc-900">{backfillResult.eligible ?? 0}</div>
                </div>
                <div>
                  <div className="text-2xs uppercase tracking-wider text-zinc-500">Newly synced</div>
                  <div className="font-mono-num text-emerald-700 font-semibold">{backfillResult.synced ?? 0}</div>
                </div>
                <div>
                  <div className="text-2xs uppercase tracking-wider text-zinc-500">Already synced</div>
                  <div className="font-mono-num text-zinc-700">{backfillResult.already_synced ?? 0}</div>
                </div>
              </div>
              {(backfillResult.skipped_no_email ?? 0) > 0 && (
                <div className="mt-2 text-2xs text-zinc-500">
                  {backfillResult.skipped_no_email} submission(s) skipped — no email captured.
                </div>
              )}
              {(backfillResult.failure_count ?? 0) > 0 && (
                <div className="mt-3">
                  <div className="text-2xs font-semibold text-red-700 mb-1">
                    {backfillResult.failure_count} failure{backfillResult.failure_count === 1 ? "" : "s"}
                    {backfillResult.failures?.length < backfillResult.failure_count && " (showing first 20)"}
                  </div>
                  <ul className="text-2xs text-red-900 bg-red-50 border border-red-200 rounded p-2 max-h-40 overflow-y-auto space-y-0.5">
                    {(backfillResult.failures || []).map((f, i) => (
                      <li key={i} className="font-mono-num">
                        {f.email} — {f.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
